import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import styled from 'styled-components';

import { ActionDropdown } from '../ActionDropdown';
import type { ActionItem } from '../ActionDropdown';
import type {
  CardFieldConfig,
  TableCardProps,
} from '../DataTable/DataTable.types';
import {
  Card,
  CardActions,
  CardBody,
  CardContainer,
  CardField,
  CardGrid,
  CardHeader,
  CardLabel,
  CardTitle,
  CardValue,
  EmptyStateContainer,
  EmptyStateIcon,
  LoadingCard,
  LoadingCardContent,
  LoadingCardLine,
} from './TableCard.styles';

const LoadingCardComponent = () => (
  <LoadingCard>
    <LoadingCardContent>
      <LoadingCardLine width="60%" />
      <LoadingCardLine width="80%" />
      <LoadingCardLine width="40%" />
      <LoadingCardLine width="90%" />
    </LoadingCardContent>
  </LoadingCard>
);

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.colorTextSecondary};
`;

export function TableCard<T extends Record<string, unknown>>({
  data,
  cardFields,
  loading = false,
  emptyState,
  rowKey = 'id',
  actionColumn,
}: TableCardProps<T>) {
  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return (record[rowKey] as string) || String(index);
  };

  const renderFieldValue = (field: CardFieldConfig<T>, record: T) => {
    const value = record[field.key];

    if (field.render) {
      return field.render(value, record);
    }

    // Default rendering based on value type
    if (value === null || value === undefined) {
      return <EmptyValue>-</EmptyValue>;
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'object' && value instanceof Date) {
      return value.toLocaleDateString();
    }

    return String(value);
  };

  const getActionsForRecord = (
    _record: T,
  ): (ActionItem | { type: 'divider' })[] => {
    if (!actionColumn || !actionColumn.render) {
      return [];
    }

    // This is a bit hacky - we need to extract actions from the action column
    // In a real implementation, you'd want to pass actions configuration separately
    return [];
  };

  if (loading) {
    return (
      <CardContainer>
        <CardGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <LoadingCardComponent key={index} />
          ))}
        </CardGrid>
      </CardContainer>
    );
  }

  if (data.length === 0 && emptyState) {
    return (
      <CardContainer>
        <EmptyStateContainer>
          <EmptyStateIcon>{emptyState.icon}</EmptyStateIcon>
          <Typography.Title
            level={5}
            style={{ margin: '0 0 8px 0', color: 'inherit' }}
          >
            {emptyState.title}
          </Typography.Title>
          {emptyState.description && (
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 16 }}
            >
              {emptyState.description}
            </Typography.Text>
          )}
          {emptyState.action && (
            <Button
              type={emptyState.action.type || 'primary'}
              icon={emptyState.action.icon}
              onClick={emptyState.action.onClick}
            >
              {emptyState.action.label}
            </Button>
          )}
        </EmptyStateContainer>
      </CardContainer>
    );
  }

  // Group fields by priority for proper ordering
  const priorityFields = cardFields.filter(
    (field) => field.priority === 'high',
  );
  const mediumFields = cardFields.filter(
    (field) => field.priority === 'medium' || !field.priority,
  );
  const lowFields = cardFields.filter((field) => field.priority === 'low');
  const orderedFields = [...priorityFields, ...mediumFields, ...lowFields];

  // Get primary field for card title
  const primaryField = priorityFields[0] || cardFields[0];

  return (
    <CardContainer>
      <CardGrid>
        {data.map((record, index) => {
          const actions = getActionsForRecord(record);

          return (
            <Card key={getRowKey(record, index)}>
              <CardHeader>
                <CardTitle>
                  {primaryField
                    ? renderFieldValue(primaryField, record)
                    : `Item ${index + 1}`}
                </CardTitle>
                {actions.length > 0 && (
                  <CardActions>
                    <ActionDropdown actions={actions} />
                  </CardActions>
                )}
              </CardHeader>

              <CardBody>
                {orderedFields.slice(1).map((field) => (
                  <CardField key={String(field.key)} $priority={field.priority}>
                    <CardLabel>{field.label}:</CardLabel>
                    <CardValue>{renderFieldValue(field, record)}</CardValue>
                  </CardField>
                ))}
              </CardBody>
            </Card>
          );
        })}
      </CardGrid>
    </CardContainer>
  );
}
